import { Entity } from '@square/ignition';
import { SpecVersionPickerContainer } from '@squareup/dex-feature-shared-main-specification-version-picker';
import {
  Badge,
  BadgeType,
  AnchoredHeading,
  Link,
  Eyebrow,
} from '@squareup/dex-ui';
import { Markdown } from '@squareup/dex-ui-markdown';
import {
  Display20,
  Heading30,
  Box,
  Paragraph30,
} from '@squareup/dex-ui-shared-base';
import { useDecision } from '@squareup/dex-ui-shared-feature-detection';
import { breakHtmlForCamelCaseReact } from '@squareup/dex-utils-text';
import clsx from 'clsx';
import React, { FC, ReactNode } from 'react';

import { EntityEnum } from '../../EntityEnum';
import { PropertyCategory, PropertyRow } from '../../PropertyRow';
import { PropertyTable } from '../../PropertyTable';

import styles from './reference-object.module.css';

interface ReferenceObjectProps {
  readonly compact?: boolean;
  readonly disableAnchors?: boolean | undefined;
  readonly entity: Entity;
  readonly eyebrow?: string | undefined;
  readonly showVersionSwitcher?: boolean | undefined;
  readonly backHref?: React.ComponentProps<typeof Link>['href'] | undefined;
  readonly breadcrumbs?: ReactNode;
}

const ReferenceObject: FC<ReferenceObjectProps> = ({
  compact = false,
  entity,
  disableAnchors,
  eyebrow,
  showVersionSwitcher = false,
  backHref,
  breadcrumbs,
}) => {
  const { decision } = useDecision('narrow_property_table');
  const narrowViewEnabled = decision.enabled;

  const validProperties =
    entity.properties?.filter((property) => !property.isRetired) || [];

  const validEnums =
    entity.enums?.filter(
      (enumValue) => enumValue.releaseStatus !== 'RETIRED'
    ) || [];

  let statusBadge = null;
  if (entity.isBeta) {
    statusBadge = (
      <Badge className={styles['status-badge']} type={BadgeType.Beta} />
    );
  } else if (entity.isDeprecated) {
    statusBadge = (
      <Badge className={styles['status-badge']} type={BadgeType.Deprecated} />
    );
  }

  const headingText = entity.isEnum ? 'Values' : 'Properties';
  const headingAnchor = entity.isEnum ? 'values' : 'properties';
  const unanchoredHeading = <Heading30>{headingText}</Heading30>;
  const heading = disableAnchors ? (
    unanchoredHeading
  ) : (
    <AnchoredHeading
      trackingId={`reference-object-content-${headingAnchor}`}
      anchor={headingAnchor}
      aria-label={`Copy link to ${headingText}`}
      aria-describedby={
        'Copies a link directly to this property into your clipboard'
      }
    >
      {unanchoredHeading}
    </AnchoredHeading>
  );

  return (
    <>
      <Box className={clsx(styles.eyebrow, breadcrumbs && styles.breadcrumbs)}>
        <Eyebrow
          backHref={backHref}
          colorVariant={breadcrumbs ? '10' : undefined}
        >
          {eyebrow}
        </Eyebrow>
        {breadcrumbs && (
          <>
            <Paragraph30 margin={{ horizontal: '1x' }}>•</Paragraph30>{' '}
            {breadcrumbs}
          </>
        )}
        {showVersionSwitcher && (
          <SpecVersionPickerContainer variant="reference" />
        )}
      </Box>
      <Box margin={{ bottom: '3x' }}>
        <Display20 data-testid="object-name-heading">
          {breakHtmlForCamelCaseReact(entity.name)}
          {statusBadge}
        </Display20>
      </Box>
      <Box margin={{ bottom: '4x' }}>
        {entity.summary && <Markdown markdown={entity.summary}></Markdown>}
        {entity.description && (
          <Box margin={{ top: '2x' }}>
            <Markdown
              testId="reference-object-description"
              markdown={entity.description}
            ></Markdown>
          </Box>
        )}
      </Box>

      {shouldDisplayHeader(entity, validEnums, validProperties) && (
        <Box
          margin={{ bottom: '2x' }}
          padding={{ bottom: '2x' }}
          border={{ line: { bottom: 'standard' } }}
        >
          {heading}
        </Box>
      )}

      {!entity.isEnum && validProperties.length > 0 && (
        <PropertyTable>
          {validProperties.map((property, i) => {
            return (
              <PropertyRow
                category={PropertyCategory.Definitions}
                compact={compact}
                disableAnchors={disableAnchors}
                key={property.name}
                property={property}
                narrowView={narrowViewEnabled}
                showBottomBorder={
                  narrowViewEnabled && i !== validProperties.length - 1
                }
              />
            );
          })}
        </PropertyTable>
      )}
      {entity.isEnum && validEnums.length > 0 && (
        <Box>
          {validEnums.map((enumValue, i, list) => {
            const border =
              i !== list.length - 1
                ? { line: { bottom: 'standard' as 'standard' } }
                : undefined;

            return (
              <Box
                key={enumValue.value}
                margin={{ top: '2x' }}
                padding={{ bottom: '2x' }}
                border={border}
              >
                <EntityEnum entityEnum={enumValue}></EntityEnum>
              </Box>
            );
          })}
        </Box>
      )}
    </>
  );
};

function shouldDisplayHeader(
  entity: Entity,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validEnums: any[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validProperties: any[]
) {
  return entity.isEnum ? validEnums.length > 0 : validProperties.length > 0;
}

export { ReferenceObject };
